import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import createUrlBuilder from 'utilities/url.builder.utils';
import { getRequest } from 'utilities/service.utils';
import DOMPurify from 'dompurify';
import AppService from '../../services/app.service.context';
import { Download20 } from '@carbon/icons-react';
import ReactShadowRoot from 'react-shadow-root';
import { getEmailAttachment } from 'utilities/helper.util';
import { fetchEmailWithDetail } from 'services/emails.service';

const createMarkup = (html: any) => {
  // First sanitize the HTML with DOMPurify
  let sanitizedHTML = DOMPurify.sanitize(html);

  // Convert HTTP and FTP links to plain text (keep the link text, remove the <a> tags)
  sanitizedHTML = sanitizedHTML.replace(
    /<a\b[^>]*href=["'](?:http:\/\/|ftp:\/\/)[^"']*["'][^>]*>(.*?)<\/a>/gi,
    '$1'
  );

  return {
    __html: sanitizedHTML
  };
};

interface ResponseBodyDownloadProps {
  html: any;
  referralId?: number;
  inboundEmailId?: number;
  enquiryId?: number;
  children: (props: {
    downloadResponseBody: () => void;
    isDownloading: boolean;
  }) => React.ReactNode;
}

export const ResponseBodyDownload: React.FC<ResponseBodyDownloadProps> = ({
  html,
  referralId,
  inboundEmailId = 0,
  children
}) => {
  const history = useHistory();
  const queryParams = new URLSearchParams(history?.location.search);
  const shareId = queryParams.get('shareId') || '';

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const downloadResponseBodyAsHTML = () => {
    const bodyContent = html;
    const referralExternalId = referralId || '';
    let htmlBody = '<!DOCTYPE html>';
    htmlBody += '<html>';
    htmlBody += '<head>';
    htmlBody += '<style>';
    htmlBody +=
      'body {font-family: Helvetica, Arial, sans-serif; font-size: 14px;}';
    htmlBody += '</style>';
    htmlBody += '</head>';
    htmlBody += '<body>';
    htmlBody += `${bodyContent}`;
    htmlBody += '</body>';
    htmlBody += '</html>';

    const blob = new Blob([htmlBody], { type: 'text/html' });
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = `Response body - ${referralExternalId}.html`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const downloadResponseBody = async () => {
    setIsDownloading(true);

    if (inboundEmailId > 0) {
      try {
        const res = await fetchEmailWithDetail({
          direction: 'inbound',
          emailId: inboundEmailId,
          downloadEmail: referralId ? false : true,
          shareId
        });
        if (res) {
          const emailBody: any = res.Attachments.find(
            (file: any) => file.mimeType === 'message/rfc822'
          );
          getEmailAttachment({
            url: emailBody.url,
            attachmentName: emailBody.name
          });
        }
      } catch (err) {
        console.error(err);
        downloadResponseBodyAsHTML();
      }
    } else {
      downloadResponseBodyAsHTML();
    }

    setIsDownloading(false);
  };

  return <>{children({ downloadResponseBody, isDownloading })}</>;
};

const HTMLViewer: React.FC<Props> = ({
  html,
  title,
  isSuperModal,
  referralId,
  inboundEmailId = 0,
  enquiryId
}) => {
  const [imagesToGet, setImagesToGet] = useState<Array<any>>([]);
  const [modifiedHTML, setModifiedHTML] = useState<any>('');
  const shadowRootRef: any = useRef();

  const updateImgSrcWithId = useCallback(
    (htmlString: any) => {
      const imagesArray: Array<any> = [];
      const imgRegex = /<img\b[^>]*>/g;
      const srcRegex = /src=["']([^"']+)["']/;
      let idCounter = 0;

      const updatedHtml = htmlString.replace(imgRegex, function (imgTag: any) {
        const srcMatch = imgTag.match(srcRegex);
        if (srcMatch) {
          const originalSrc = srcMatch[1];
          imagesArray.push(originalSrc);

          const imgId = `${
            isSuperModal ? 'supermodal_' : ''
          }imgId_${idCounter++}`;

          const imgWithId = imgTag.replace('>', ' id="' + imgId + '">');
          return imgWithId;
        }
        return imgTag;
      });

      setImagesToGet(imagesArray);
      return updatedHtml;
    },
    [isSuperModal]
  );

  useEffect(() => {
    if (html) {
      const updatedHTML = updateImgSrcWithId(html);
      setModifiedHTML(updatedHTML);
    }
  }, [html, updateImgSrcWithId]);

  useEffect(() => {
    const fetchImages = async () => {
      for (let i = 0; i < imagesToGet.length; i++) {
        const urlPattern = new RegExp(
          '^https:\\/\\/' + // validate protocol (only https://)
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
            '(\\#[-a-z\\d_]*)?$', // validate fragment
          'i'
        );
        if (!urlPattern.test(imagesToGet[i])) {
          continue;
        }

        const urlBuilder = createUrlBuilder(
          `/system/image/proxy?imageUrl=${imagesToGet[i]}&enquiryId=${enquiryId}`
        );
        const newSrc =
          urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
        const shadowRoot: any = shadowRootRef?.current?.shadowRoot;
        const imgTag = shadowRoot?.querySelector(
          `#${isSuperModal ? 'supermodal_' : ''}imgId_${i}`
        );

        if (imgTag && newSrc) {
          imgTag.src = newSrc;
        }
      }
    };

    if (imagesToGet?.length > 0) {
      fetchImages();
    }
  }, [imagesToGet, isSuperModal, enquiryId]);

  return (
    <div className="html-viewer">
      <div
        className={`html-viewer-header ${
          isSuperModal ? 'supermodal-header' : ''
        }`}
      >
        <div className="html-viewer-header-file-name">{title}</div>
        <div className="html-viewer-header-controls">
          <ResponseBodyDownload
            html={html}
            referralId={referralId}
            inboundEmailId={inboundEmailId}
            enquiryId={enquiryId}
          >
            {({ downloadResponseBody, isDownloading }) => (
              <div
                title={'Download response body'}
                className={`html-viewer-header-controls-btn ${
                  isDownloading ? 'disabled' : ''
                }`}
                onClick={() => downloadResponseBody()}
              >
                <Download20 />
              </div>
            )}
          </ResponseBodyDownload>
        </div>
      </div>
      <div
        className={`html-viewer-content ${
          isSuperModal ? 'html-viewer-supermodal-content' : ''
        }`}
      >
        <ReactShadowRoot ref={shadowRootRef}>
          <div dangerouslySetInnerHTML={createMarkup(modifiedHTML)} />
        </ReactShadowRoot>
      </div>
    </div>
  );
};

type Props = {
  html: any;
  title?: string;
  isSuperModal?: boolean;
  referralId?: number;
  inboundEmailId?: number;
  enquiryId?: number;
};

export default React.memo(HTMLViewer);
